import type { FC } from "react";
import { useGetServiceAccount } from "@/queries/service-account-queries";
import { useMemo } from "react";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { Text } from "@/components/podkit/typography/Text";
import { useMembersByIds } from "@/queries/organization-queries";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Timestamp } from "@bufbuild/protobuf";
import { formatShortDate } from "@/format/time";

export const ServiceAccountSummary: FC<{ serviceAccountId: string }> = ({ serviceAccountId }) => {
    const { data: serviceAccount } = useGetServiceAccount({ serviceAccountId });
    const creatorIds = useMemo(
        () => (serviceAccount?.creator?.id ? [serviceAccount.creator.id] : []),
        [serviceAccount?.creator?.id],
    );
    const { members: membersMap } = useMembersByIds(creatorIds);

    const creator = useMemo(() => {
        if (!serviceAccount?.creator?.id) {
            return null;
        }
        return membersMap.get(serviceAccount.creator.id) ?? null;
    }, [serviceAccount?.creator?.id, membersMap]);

    const createdDate = serviceAccount?.createdAt
        ? formatShortDate(new Timestamp(serviceAccount.createdAt).toDate(), true)
        : null;

    if (!serviceAccount) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <ServiceAccountAvatar id={serviceAccount.id} size={32} />
                <Text className="text-lg font-medium text-content-primary">{serviceAccount.name}</Text>
            </div>

            {serviceAccount.description && (
                <Text className="text-base text-content-strong">{serviceAccount.description}</Text>
            )}

            {createdDate && (
                <div className="flex items-center gap-1.5 text-sm text-content-strong">
                    <span>Created on {createdDate}</span>
                    {creator && (
                        <>
                            <span>by</span>
                            <div className="flex items-center gap-1.5">
                                <Avatar size={16}>
                                    {creator.avatarUrl && (
                                        <AvatarImage src={creator.avatarUrl} alt={`${creator.fullName}'s avatar`} />
                                    )}
                                    <AvatarFallback>
                                        <AvatarInitials name={creator.fullName} size={16} />
                                    </AvatarFallback>
                                </Avatar>
                                <span>{creator.fullName}</span>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
